import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { useI18n } from '../hooks/useI18n'
import { resolveAvatar } from '../lib/avatar'
import phBol from '../assets/avatar-placeholders/bol.png'
import phCoffee from '../assets/avatar-placeholders/coffee.png'
import phMan from '../assets/avatar-placeholders/man.png'
import phWoman from '../assets/avatar-placeholders/woman.png'
import phWoman2 from '../assets/avatar-placeholders/woman2.png'

const DECOYS = [phWoman, phMan, phCoffee, phWoman2, phBol]

/** Stable decoy per attendee — hashed from the id so the stack never shuffles. */
const decoyFor = (id: string, index: number) => {
  const key = String(id || index)
  let hash = 0
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0
  return DECOYS[hash % DECOYS.length]
}

/**
 * Who is coming — faces, capped, with a count.
 *
 * `anonymous` (a guest) sees HOW MANY, never WHO. The endpoint already returns
 * only an opaque id and an avatar URL, never a name; this swaps the photos for
 * placeholders so social proof survives without handing a stranger real
 * members' pictures.
 */
export default function AttendeeAvatars({
  eventId, max = 5, refreshKey = 0, anonymous = false
}: { eventId: string; max?: number; refreshKey?: number; anonymous?: boolean }) {
  const { t } = useI18n()
  const [data, setData] = useState<{ total: number; attendees: Array<{ id: string; avatar_url: string | null }> } | null>(null)

  useEffect(() => {
    let active = true
    if (!eventId) return undefined
    api.getEventAttendees(eventId)
      .then((result) => { if (active) setData(result) })
      .catch((error) => {
        console.warn('[tc][attendees] failed', { eventId, error })
        if (active) setData(null)
      })
    return () => { active = false }
  }, [eventId, refreshKey])

  const total = Number(data?.total || 0)
  if (!total) return null

  const raw = (data?.attendees || []).slice(0, max)
  const shown = anonymous
    ? raw.map((entry, index) => ({ ...entry, avatar_url: decoyFor(entry.id, index) }))
    : raw
  const overflow = total - shown.length

  return (
    <div className="mt-2 flex items-center gap-2">
      <span className="text-sm text-muted">{t('ev.people')} {total}</span>
      <div className="flex">
        {shown.map((attendee, index) => (
          <img
            key={attendee.id || index}
            // resolveAvatar swaps an OSS /placeholders/ URL for the copy
            // already in this bundle — no request at all for the stock faces,
            // which is most of them.
            src={resolveAvatar(attendee.avatar_url) || DECOYS[0]}
            alt=""
            className="-ml-2 h-7 w-7 rounded-full border border-bg object-cover first:ml-0"
            style={{ zIndex: max - index }}
          />
        ))}
        {overflow > 0 ? (
          <span className="-ml-2 flex h-7 w-7 items-center justify-center rounded-full border border-bg bg-card-2 text-2xs text-muted">
            +{overflow}
          </span>
        ) : null}
      </div>
    </div>
  )
}
